import type firebase from 'firebase/app'
import {
  requireFirebaseAuth,
  requireFirebaseFirestore,
  requireFirebaseStorage,
} from './client'
import { isFirebasePermissionDeniedError } from './errors'
import {
  normalizeDashboardUserProfile,
  normalizeDashboardBook,
  type DashboardActivationKey,
  type DashboardBook,
  type DashboardUserProfile,
} from './normalizers'
import { traceLearningPerformance } from '@shared/learning/learning-performance-trace'
import { ensureLearnerTrialSafe } from './trial'
import { getHanaspeakApiBaseUrl } from './functions-api'

type Firestore = firebase.firestore.Firestore

interface DashboardDeferredProfileFields {
  readonly count_daily_activities: number
  readonly daily_activities: NonNullable<DashboardUserProfile['daily_activities']>
  readonly personalization: Record<string, unknown> | null
  readonly selfstudy_chapter_learning: DashboardUserProfile['selfstudy_chapter_learning']
}

export interface DashboardUserProfileBootstrap {
  readonly deferredProfileFields: Promise<DashboardDeferredProfileFields>
  readonly profile: DashboardUserProfile
}

function getCurrentWeekStartTimestamp(date: Date) {
  const startOfDay = new Date(date)
  startOfDay.setHours(0, 0, 0, 0)

  const currentDay = startOfDay.getDay()
  const dayOffset = currentDay === 0 ? -6 : 1 - currentDay

  startOfDay.setDate(startOfDay.getDate() + dayOffset)

  return startOfDay.getTime()
}

function getAvatarFileExtension(fileName: string) {
  const lastDotIndex = fileName.lastIndexOf('.')

  if (lastDotIndex < 0) {
    return ''
  }

  return fileName.slice(lastDotIndex)
}

function getCurrentUserCreationTimestamp(user: firebase.User | null) {
  const creationTime = user?.metadata?.creationTime

  if (!creationTime) {
    return Date.now()
  }

  const parsedValue = Date.parse(creationTime)

  return Number.isNaN(parsedValue) ? Date.now() : parsedValue
}

function normalizeActivityCount(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.max(0, Math.round(value))
    : 0
}

function getStoredDashboardActivityCount(userData: Record<string, unknown>) {
  const storedCount = normalizeActivityCount(userData.count_daily_activities)

  if (storedCount > 0) {
    return storedCount
  }

  return Array.isArray(userData.daily_activities)
    ? userData.daily_activities.length
    : storedCount
}

function getPermissionSafeResult<T>({
  fallback,
  task,
}: {
  readonly fallback: T
  readonly task: () => Promise<T>
}) {
  return task().catch((error) => {
    if (isFirebasePermissionDeniedError(error)) {
      return fallback
    }

    throw error
  })
}

function getDashboardActivationKeyStatus({
  active,
  endAt,
}: {
  readonly active: unknown
  readonly endAt: number | null
}): NonNullable<DashboardActivationKey['status']> {
  if (typeof endAt === 'number' && endAt <= Date.now()) {
    return 'expired'
  }

  if (
    (active === true || active === 'activated') &&
    typeof endAt === 'number' &&
    endAt > Date.now()
  ) {
    return 'active'
  }

  if (active === false || typeof active === 'string' || endAt === null) {
    return 'inactive'
  }

  return 'unknown'
}

async function getDashboardActivationKey({
  firestore,
  userData,
  userId,
}: {
  readonly firestore: Firestore
  readonly userData: Record<string, unknown>
  readonly userId: string
}): Promise<DashboardActivationKey | null> {
  if (typeof userData.activationCode !== 'string' || !userData.activationCode) {
    return null
  }

  const activationSnapshot = await traceLearningPerformance({
    event: 'profile.activationKey.query',
    getDetails: (snapshot) => ({ docs: snapshot.size }),
    task: () =>
      firestore
        .collection('activation_keys')
        .where('userId', '==', userId)
        .get(),
  })

  const activationDocument = activationSnapshot.docs.find(
    (document) => document.data().activationCode === userData.activationCode,
  )

  if (!activationDocument) {
    return null
  }

  const activationData = activationDocument.data() as Record<string, unknown>
  const endAt = typeof activationData.endAt === 'number' ? activationData.endAt : null

  return {
    active:
      typeof activationData.active === 'boolean' ||
      typeof activationData.active === 'string'
        ? activationData.active
        : undefined,
    createdAt:
      typeof activationData.createdAt === 'number'
        ? activationData.createdAt
        : undefined,
    duration:
      typeof activationData.duration === 'number'
        ? activationData.duration
        : undefined,
    endAt: endAt ?? undefined,
    id: activationDocument.id,
    keyNumber:
      typeof activationData.keyNumber === 'string'
        ? activationData.keyNumber
        : undefined,
    startAt:
      typeof activationData.startAt === 'number'
        ? activationData.startAt
        : undefined,
    status: getDashboardActivationKeyStatus({
      active: activationData.active,
      endAt,
    }),
    type:
      typeof activationData.type === 'string'
        ? activationData.type
        : undefined,
  }
}

async function getDashboardCreatedAt({
  fallbackCreatedAt,
  firestore,
  userId,
}: {
  readonly fallbackCreatedAt: number
  readonly firestore: Firestore
  readonly userId: string
}) {
  return getPermissionSafeResult({
    fallback: fallbackCreatedAt,
    task: async () => {
      const learnerStartRef = firestore.collection('learner_time_start').doc(userId)
      const learnerStartDocument = await traceLearningPerformance({
        event: 'profile.learnerStart.get',
        getDetails: (document) => ({ exists: document.exists }),
        task: () => learnerStartRef.get(),
      })

      if (learnerStartDocument.exists) {
        const learnerStartData = learnerStartDocument.data() as Record<string, unknown>

        return typeof learnerStartData.createdAt === 'number'
          ? learnerStartData.createdAt
          : fallbackCreatedAt
      }

      await traceLearningPerformance({
        event: 'profile.learnerStart.create',
        task: () => learnerStartRef.set({ createdAt: fallbackCreatedAt }),
      })

      return fallbackCreatedAt
    },
  })
}

async function getDashboardPersonalization({
  firestore,
  userData,
  userId,
}: {
  readonly firestore: Firestore
  readonly userData: Record<string, unknown>
  readonly userId: string
}) {
  const stored = userData.personalization
  const fallback = stored && typeof stored === 'object' && !Array.isArray(stored)
    ? stored as Record<string, unknown>
    : null
  return getPermissionSafeResult<Record<string, unknown> | null>({
    fallback,
    task: async () => {
      const personalizationDocument = await traceLearningPerformance({
        event: 'profile.personalization.get',
        getDetails: (document) => ({ exists: document.exists }),
        task: () =>
          firestore
            .collection('personalization')
            .doc(userId)
            .get(),
      })

      return personalizationDocument.exists
        ? ((personalizationDocument.data() as Record<string, unknown>) ?? fallback)
        : fallback
    },
  })
}

async function getDashboardActivitySummary({
  userData,
}: {
  readonly userData: Record<string, unknown>
}) {
  const fallback = {
    currentWeekActivities: [] as NonNullable<DashboardUserProfile['daily_activities']>,
    totalActivitiesCount: getStoredDashboardActivityCount(userData),
  }
  try {
    const user = requireFirebaseAuth().currentUser
    if (!user) return fallback
    const idToken = await user.getIdToken()
    return await traceLearningPerformance({
      event: 'profile.dailyActivities.currentWeek',
      task: async (): Promise<{
        readonly currentWeekActivities: NonNullable<DashboardUserProfile['daily_activities']>
        readonly totalActivitiesCount: number
      }> => {
        const query = new URLSearchParams({
          weekStart: String(getCurrentWeekStartTimestamp(new Date())),
        })
        const response = await fetch(`${getHanaspeakApiBaseUrl()}/learning/activity/week?${query}`, {
          headers: { Authorization: `Bearer ${idToken}` },
        })
        const payload = await response.json().catch(() => null) as {
          readonly activities?: NonNullable<DashboardUserProfile['daily_activities']>
          readonly totalActivitiesCount?: number
        } | null
        if (!response.ok || !Array.isArray(payload?.activities)) {
          throw new Error('Unable to load study activity right now.')
        }
        return {
          currentWeekActivities: payload.activities,
          totalActivitiesCount: typeof payload.totalActivitiesCount === 'number'
            ? payload.totalActivitiesCount
            : fallback.totalActivitiesCount,
        }
      },
    })
  } catch (error) {
    console.warn('[learning-activity] weekly summary unavailable', error)
    return fallback
  }
}

async function getDashboardSelfStudyChapterLearning({
  firestore,
  learningProgram,
  userId,
}: {
  readonly firestore: Firestore
  readonly learningProgram?: unknown
  readonly userId: string
}): Promise<DashboardUserProfile['selfstudy_chapter_learning']> {
  if (typeof learningProgram !== 'string' || !learningProgram) {
    return null
  }

  return getPermissionSafeResult<DashboardUserProfile['selfstudy_chapter_learning']>({
    fallback: null,
    task: async () => {
      const selfStudySnapshot = await traceLearningPerformance({
        event: 'profile.selfStudyChapterLearning.query',
        getDetails: (snapshot) => ({ docs: snapshot.size }),
        task: () =>
          firestore
            .collection('selfstudy_chapter_learning')
            .where('userId', '==', userId)
            .where('learning_program', '==', learningProgram)
            .limit(1)
            .get(),
      })

      if (selfStudySnapshot.empty) {
        return null
      }

      const selfStudyDocument = selfStudySnapshot.docs[0]

      return {
        ...(selfStudyDocument.data() as Omit<
          NonNullable<DashboardUserProfile['selfstudy_chapter_learning']>,
          'id'
        >),
        id: selfStudyDocument.id,
      }
    },
  })
}

async function getDeferredDashboardProfileFields({
  firestore,
  userData,
  userId,
}: {
  readonly firestore: Firestore
  readonly userData: Record<string, unknown>
  readonly userId: string
}): Promise<DashboardDeferredProfileFields> {
  const [personalizationData, activitySummary, selfStudyChapterLearning] =
    await Promise.all([
      getDashboardPersonalization({ firestore, userData, userId }),
      getDashboardActivitySummary({ userData }),
      getDashboardSelfStudyChapterLearning({
        firestore,
        learningProgram: userData.current_learning_program,
        userId,
      }),
    ])

  return {
    count_daily_activities: activitySummary.totalActivitiesCount,
    daily_activities: activitySummary.currentWeekActivities,
    personalization: personalizationData,
    selfstudy_chapter_learning: selfStudyChapterLearning,
  }
}

export const getPublishedBooks = async (): Promise<DashboardBook[]> => {
  const firestore = requireFirebaseFirestore()
  const snapshot = await traceLearningPerformance({
    event: 'profile.publishedBooks.query',
    getDetails: (querySnapshot) => ({ docs: querySnapshot.size }),
    task: () =>
      firestore
        .collection('books')
        .where('status', '==', 'published')
        .orderBy('sort', 'asc')
        .get(),
  })

  const books = snapshot.docs.map((document) =>
    normalizeDashboardBook({
      ...(document.data() as Omit<DashboardBook, 'id'>),
      id: document.id,
    }),
  )

  return books.sort((left, right) => {
    const leftSort = typeof left.sort === 'number' ? left.sort : 0
    const rightSort = typeof right.sort === 'number' ? right.sort : 0

    return leftSort - rightSort
  })
}

export const updateDashboardProfile = async (
  information: Record<string, unknown>,
) => {
  const auth = requireFirebaseAuth()

  if (!auth.currentUser) {
    throw new Error('Not authorized')
  }

  const preferenceFields = new Set(['language_default', 'accent_default', 'my_daily_goal'])
  const fields = Object.keys(information)
  if (fields.length > 0 && fields.every((field) => preferenceFields.has(field))) {
    const idToken = await auth.currentUser.getIdToken()
    const response = await fetch(`${getHanaspeakApiBaseUrl()}/learning/profile/preferences`, {
      body: JSON.stringify(information),
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      method: 'POST',
    })
    if (!response.ok) {
      const payload = await response.json().catch(() => null) as { error?: unknown } | null
      throw new Error(
        typeof payload?.error === 'string'
          ? payload.error
          : 'Unable to save learning preferences right now.',
      )
    }
    return
  }

  const firestore = requireFirebaseFirestore()
  await firestore.doc(`users/${auth.currentUser.uid}`).set(
    {
      ...information,
      email: auth.currentUser.email,
    },
    { merge: true },
  )
}

export async function selectDashboardProgram(bookId: string): Promise<void> {
  const auth = requireFirebaseAuth()
  if (!auth.currentUser) {
    throw new Error('Not authorized')
  }

  const idToken = await auth.currentUser.getIdToken()
  const response = await fetch(`${getHanaspeakApiBaseUrl()}/learning/program/select`, {
    body: JSON.stringify({ bookId }),
    headers: {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    method: 'POST',
  })
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { error?: unknown } | null
    throw new Error(
      typeof payload?.error === 'string'
        ? payload.error
        : 'Unable to select the learning program right now.',
    )
  }
}

export const uploadDashboardAvatar = async ({
  file,
  previousImageUrl,
}: {
  readonly file: File
  readonly previousImageUrl?: string
}) => {
  const auth = requireFirebaseAuth()
  const storage = requireFirebaseStorage()

  if (!auth.currentUser) {
    throw new Error('Not authorized')
  }

  const extension = getAvatarFileExtension(file.name)
  const avatarPath = `avatars/${auth.currentUser.uid}-${Date.now()}${extension}`
  const avatarReference = storage.ref().child(avatarPath)

  await avatarReference.put(file)

  const imageUrl = await avatarReference.getDownloadURL()

  await updateDashboardProfile({ imageUrl })

  if (previousImageUrl) {
    try {
      await storage.refFromURL(previousImageUrl).delete()
    } catch {
      // Ignore avatar cleanup failures after the new image is already live.
    }
  }

  return imageUrl
}

export const getDashboardUserProfileBootstrap =
  async (): Promise<DashboardUserProfileBootstrap> => {
    const auth = requireFirebaseAuth()
    const firestore = requireFirebaseFirestore()

    if (!auth.currentUser) {
      throw new Error('Not authorized')
    }

    const userId = auth.currentUser.uid
    const userDocument = await traceLearningPerformance({
      event: 'profile.user.get',
      getDetails: (document) => ({ exists: document.exists }),
      task: () => firestore.doc(`users/${userId}`).get(),
    })
    const userData = userDocument.exists
      ? (userDocument.data() as Record<string, unknown>)
      : {}

    if (typeof userData.activationCode !== 'string' || !userData.activationCode) {
      const trial = await ensureLearnerTrialSafe()
      if (trial?.activationCode) {
        userData.activationCode = trial.activationCode
      }
    }

    const [activationKey, createdAt] = await Promise.all([
      getDashboardActivationKey({ firestore, userData, userId }),
      getDashboardCreatedAt({
        fallbackCreatedAt: getCurrentUserCreationTimestamp(auth.currentUser),
        firestore,
        userId,
      }),
    ])

    const profile = normalizeDashboardUserProfile({
      ...(userData as Omit<DashboardUserProfile, 'id'>),
      activation_key: activationKey,
      count_daily_activities: getStoredDashboardActivityCount(userData),
      createdAt,
      daily_activities: [],
      email:
        typeof userData.email === 'string' ? userData.email : auth.currentUser.email,
      id: userDocument.id || userId,
      personalization: null,
      selfstudy_chapter_learning: null,
    }) as DashboardUserProfile

    return {
      deferredProfileFields: getDeferredDashboardProfileFields({
        firestore,
        userData,
        userId,
      }),
      profile,
    }
  }

export const getDashboardUserProfile = async (): Promise<DashboardUserProfile> => {
  const { deferredProfileFields, profile } = await getDashboardUserProfileBootstrap()
  const deferredFields = await deferredProfileFields

  return normalizeDashboardUserProfile({
    ...profile,
    ...deferredFields,
  }) as DashboardUserProfile
}
